import React from 'react';
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';

interface Props {
  titulo: string;
  aberto: boolean;
  /** Obrigatórios em branco AGORA — o chip nunca some: zerado, vira verde. */
  pendentes: { papel: string; rotulo: string }[];
  /** Com valor, mas sem lastro na fonte. */
  paraConferir: number;
  /** O que o bloco fechado mostra: rótulo e valor dos preenchidos. */
  resumo: { papel: string; rotulo: string; valor: string }[];
  /** Chip livre no lugar da contagem (ex.: a soma do escopo). */
  chip?: React.ReactNode;
  irPara: (papel: string) => void;
  children: React.ReactNode;
}

/**
 * Um bloco da ficha (Contratada, Comercial, Dados bancários...).
 *
 * Fechado, ele não esconde trabalho: o resumo fica FORA do painel (o painel
 * fechado leva display:none e sairia do Ctrl+F) e diz o que falta — clicável —
 * ou o que já está lá.
 */
export default function BlocoFicha({
  titulo,
  aberto,
  pendentes,
  paraConferir,
  resumo,
  chip,
  irPara,
  children,
}: Props) {
  return (
    <AccordionItem border="none" mb={3}>
      <Box borderWidth="1px" borderColor="gray.100" borderRadius="12px" overflow="hidden">
        <AccordionButton px={4} py={3} _hover={{ bg: 'gray.50' }}>
          <AccordionIcon mr={2} />
          <Text flex={1} textAlign="left" fontWeight={600} fontSize="14px">
            {titulo}
          </Text>
          {paraConferir > 0 && (
            <Badge colorScheme="red" fontSize="10px" mr={2} textTransform="none">
              {paraConferir} para conferir
            </Badge>
          )}
          {chip ?? (
            <Badge
              colorScheme={pendentes.length ? 'orange' : 'green'}
              fontSize="10px"
              textTransform="none"
            >
              {pendentes.length ? `${pendentes.length} em branco` : '✓ completo'}
            </Badge>
          )}
        </AccordionButton>

        {!aberto && (
          <Box px={4} pb={3}>
            {pendentes.length > 0 ? (
              <Text fontSize="12px" color="orange.600">
                Falta preencher:{' '}
                {pendentes.map((p, i) => (
                  <React.Fragment key={p.papel}>
                    {i > 0 && ', '}
                    <Text
                      as="button"
                      type="button"
                      textDecoration="underline"
                      onClick={() => irPara(p.papel)}
                    >
                      {p.rotulo}
                    </Text>
                  </React.Fragment>
                ))}
              </Text>
            ) : (
              <Wrap spacing={3}>
                {resumo.map((r, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <WrapItem key={`${r.papel}-${i}`}>
                    <Text
                      as="button"
                      type="button"
                      fontSize="12px"
                      color="gray.600"
                      textAlign="left"
                      onClick={() => irPara(r.papel)}
                    >
                      <Text as="span" color="gray.400">
                        {r.rotulo}:{' '}
                      </Text>
                      {r.valor.length > 60 ? `${r.valor.slice(0, 60)}…` : r.valor}
                    </Text>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>
        )}

        <AccordionPanel px={4} pb={4} pt={1}>
          {children}
        </AccordionPanel>
      </Box>
    </AccordionItem>
  );
}
